import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.js';
import { api } from '../api.js';
import {
  FileText, Mail, Lock, Eye, EyeOff, ArrowRight, User,
  AlertCircle, Check, CheckCircle2, ChevronDown, X,
  Sparkles, BarChart2, Layers, Clock,
} from 'lucide-react';
import '../styles/login.css';
import '../styles/register.css';

const COUNTRIES = [
  { flag: '🇲🇽', name: 'México', code: '+52' },
  { flag: '🇺🇸', name: 'Estados Unidos', code: '+1' },
  { flag: '🇨🇴', name: 'Colombia', code: '+57' },
  { flag: '🇦🇷', name: 'Argentina', code: '+54' },
  { flag: '🇨🇱', name: 'Chile', code: '+56' },
  { flag: '🇵🇪', name: 'Perú', code: '+51' },
  { flag: '🇪🇸', name: 'España', code: '+34' },
  { flag: '🇧🇷', name: 'Brasil', code: '+55' },
  { flag: '🇻🇪', name: 'Venezuela', code: '+58' },
  { flag: '🇬🇧', name: 'Reino Unido', code: '+44' },
  { flag: '🇨🇦', name: 'Canadá', code: '+1' },
  { flag: '🇩🇪', name: 'Alemania', code: '+49' },
];

const STRENGTH_LABELS = ['', 'Muy débil', 'Débil', 'Buena', 'Fuerte'];
const STRENGTH_CLS = ['s0', 's1', 's2', 's3', 's4'];

function getPwStrength(pw) {
  if (!pw) return { score: 0, label: '', cls: 's0' };
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  const score = Math.min(s, 4);
  return { score, label: STRENGTH_LABELS[score], cls: STRENGTH_CLS[score] };
}

const validateName = (v) => (!v.trim() ? 'El nombre es obligatorio.' : (v.trim().length < 2 ? 'Mínimo 2 caracteres.' : ''));
const validateEmail = (v) => (!v ? 'El email es obligatorio.' : (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Ingresa un email válido.' : ''));
const validatePhone = (v) => (!v ? 'El teléfono es obligatorio.' : (!/^\d{7,15}$/.test(v.replace(/\s/g, '')) ? 'Ingresa un número válido (7-15 dígitos).' : ''));
const validatePassword = (v) => (!v ? 'La contraseña es obligatoria.' : (v.length < 8 ? 'Mínimo 8 caracteres.' : (getPwStrength(v).score < 2 ? 'La contraseña es muy débil.' : '')));

function PlanCard() {
  return (
    <div className="lp-deco-card lp-deco-card--plan">
      <div className="lp-deco-card__label">Plan Trial · 14 días gratis</div>
      {[
        { label: '1,000 renders/mes', ok: true },
        { label: 'API REST completa', ok: true },
        { label: 'Plantillas ilimitadas', ok: true },
        { label: 'Soporte por email', ok: true },
        { label: 'Custom branding', ok: false },
      ].map(({ label, ok }) => (
        <div key={label} className="rp-deco-plan-item">
          <span style={{ color: ok ? '#94A3B8' : '#334155', fontSize: '0.72rem' }}>{label}</span>
          <div className="rp-deco-check" style={!ok ? { opacity: 0.3 } : {}}><Check size={8} strokeWidth={3} /></div>
        </div>
      ))}
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(51,65,85,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.68rem', color: '#475569' }}>Sin tarjeta de crédito</span>
        <div className="lp-deco-pulse" />
      </div>
    </div>
  );
}

function OnboardCard() {
  return (
    <div className="lp-deco-card lp-deco-card--onboard">
      <div className="lp-deco-card__label">Onboarding</div>
      {[
        { n: '1', label: 'Crea tu cuenta', state: 'active' },
        { n: '2', label: 'Configura tu tenant', state: 'todo' },
        { n: '3', label: 'Sube tu plantilla', state: 'todo' },
        { n: '4', label: 'Primer render', state: 'todo' },
      ].map(({ n, label, state }) => (
        <div key={n} className="rp-onboard-step">
          <div className={`rp-onboard-num ${state}`}>{n}</div>
          <span style={{ color: state === 'active' ? '#CBD5E1' : '#475569', fontSize: '0.72rem' }}>{label}</span>
          {state === 'active' && <span style={{ marginLeft: 'auto', fontSize: '0.62rem', color: '#818cf8', background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.25)', borderRadius: 4, padding: '1px 5px' }}>ahora</span>}
        </div>
      ))}
    </div>
  );
}

const PARTICLES = Array.from({ length: 14 }, (_, i) => ({
  id: i,
  x: 8 + Math.abs(Math.sin(i * 97.3) * 84),
  y: 8 + Math.abs(Math.cos(i * 97.3) * 84),
  size: 1.5 + (i % 3),
  color: i % 3 === 0 ? '#4F46E5' : i % 3 === 1 ? '#22D3EE' : '#8B5CF6',
  opacity: 0.12 + (i % 5) * 0.06,
}));

export default function RegisterPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [plan, setPlan] = useState('');
  const [terms, setTerms] = useState(false);
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [countryOpen, setCountryOpen] = useState(false);

  const [plans, setPlans] = useState([]);

  const [nameErr, setNameErr] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [termsErr, setTermsErr] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const [formState, setFormState] = useState('idle');
  const [serverErr, setServerErr] = useState('');

  const nameRef = useRef(null);
  const countryRef = useRef(null);

  const strength = getPwStrength(password);
  const selectedPlan = plans.find((p) => p.id === plan);
  const selectedPlanMonthlyPrice = Number(selectedPlan?.monthlyPrice || 0);
  const selectedPlanRequiresPayment = Number.isFinite(selectedPlanMonthlyPrice) && selectedPlanMonthlyPrice > 0;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await api.getPublicPlans();
        const items = Array.isArray(res?.items) ? res.items : [];
        setPlans(items);
        if (!plan && items.length) {
          const starter = items.find((p) => String(p.code || '').toLowerCase() === 'starter');
          setPlan(starter?.id || items[0].id);
        }
      } catch {
        setPlans([]);
      }
    };
    loadPlans();
  }, [plan]);

  useEffect(() => { if (nameTouched) setNameErr(validateName(name)); }, [name, nameTouched]);
  useEffect(() => { if (emailTouched) setEmailErr(validateEmail(email)); }, [email, emailTouched]);
  useEffect(() => { if (phoneTouched) setPhoneErr(validatePhone(phone)); }, [phone, phoneTouched]);
  useEffect(() => { if (passwordTouched) setPasswordErr(validatePassword(password)); }, [password, passwordTouched]);

  useEffect(() => {
    const handler = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCountryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (formState === 'success') {
      const t = setTimeout(() => navigate('/templates'), 1400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [formState, navigate]);

  const pwRules = [
    { label: '8+ caracteres', met: password.length >= 8 },
    { label: 'Mayúscula', met: /[A-Z]/.test(password) },
    { label: 'Número', met: /[0-9]/.test(password) },
    { label: 'Símbolo', met: /[^A-Za-z0-9]/.test(password) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNameTouched(true);
    setEmailTouched(true);
    setPhoneTouched(true);
    setPasswordTouched(true);

    const nErr = validateName(name);
    const eErr = validateEmail(email);
    const phErr = validatePhone(phone);
    const pErr = validatePassword(password);

    setNameErr(nErr);
    setEmailErr(eErr);
    setPhoneErr(phErr);
    setPasswordErr(pErr);

    if (!terms) {
      setTermsErr('Debes aceptar los términos para continuar.');
      return;
    }
    setTermsErr('');

    if (nErr || eErr || phErr || pErr) return;

    setFormState('loading');
    setServerErr('');

    try {
      const res = await api.register({
        name,
        email,
        password,
        phone: phone.replace(/\s/g, ''),
        countryCode: country.code,
        countryName: country.name,
        planId: plan || undefined,
        termsAccepted: true,
      });
      setAuth(res.token, res.user);

      if (selectedPlanRequiresPayment && plan) {
        const paymentSession = await api.createStripeCheckoutSession({
          planId: plan,
          successUrl: `${window.location.origin}/templates?billing=success`,
          cancelUrl: `${window.location.origin}/register?billing=cancel`,
        });
        if (paymentSession?.checkoutUrl) {
          window.location.href = paymentSession.checkoutUrl;
          return;
        }
      }

      setFormState('success');
    } catch (err) {
      setFormState('error');
      setServerErr(err?.message || 'No fue posible crear la cuenta.');
    }
  };

  const isLoading = formState === 'loading';

  return (
    <div className="lp-page">
      <div className="lp-brand">
        {PARTICLES.map((p) => (
          <div key={p.id} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, borderRadius: '50%', background: p.color, opacity: p.opacity, pointerEvents: 'none', zIndex: 1 }} />
        ))}

        <Link to="/" className="lp-brand__top">
          <div className="lp-brand__logo"><FileText size={16} color="white" /></div>
          <span className="lp-brand__name">ReportEngine</span>
        </Link>

        <div className="lp-brand__hero">
          <div className="lp-brand__badge"><span className="lp-brand__badge-dot" />14 días gratis · Sin tarjeta de crédito</div>
          <h2 className="lp-brand__headline">Empieza a generar<br />documentos<br /><span>en minutos.</span></h2>
          <p className="lp-brand__sub">Crea tu cuenta, sube tu primera plantilla y obtén tu primer PDF en menos de 5 minutos. Sin configuración compleja.</p>

          <div className="lp-features">
            {[
              { cls: 'c1', Icon: Sparkles, title: 'Trial completo por 14 días', desc: 'Acceso a todas las funcionalidades. Sin restricciones durante el periodo de prueba.' },
              { cls: 'c2', Icon: Layers, title: 'Plantillas Handlebars + CSS', desc: 'Diseña con HTML/CSS estándar. Variables tipadas con Handlebars.' },
              { cls: 'c3', Icon: BarChart2, title: 'Analytics y historial', desc: 'Dashboard con métricas de uso, descargas y logs por tenant.' },
              { cls: 'c4', Icon: Clock, title: 'Onboarding guiado', desc: 'Wizard de configuración paso a paso. Listos en < 5 min.' },
            ].map(({ cls, Icon, title, desc }) => (
              <div key={title} className="lp-feature-item">
                <div className={`lp-feature-icon ${cls}`}><Icon size={13} /></div>
                <div className="lp-feature-text"><strong>{title}</strong><span>{desc}</span></div>
              </div>
            ))}
          </div>
        </div>

        <div className="lp-stats">
          {[{ value: '14d', label: 'Trial gratis' }, { value: '< 5min', label: 'Setup inicial' }, { value: '0', label: 'Tarjetas requeridas' }].map((s) => (
            <div key={s.label}><div className="lp-stat__value">{s.value}</div><div className="lp-stat__label">{s.label}</div></div>
          ))}
        </div>

        <PlanCard />
        <OnboardCard />
      </div>

      <div className="lp-form-panel">
        <div className="lp-form-box">
          <div className="rp-steps"><div className="rp-step-dot active" /><div className="rp-step-dot" /><div className="rp-step-dot" /></div>

          <div className="lp-form-header">
            <div className="lp-form-header__logo"><FileText size={22} color="white" /></div>
            <h1>{formState === 'success' ? '¡Cuenta creada!' : 'Crear cuenta gratis'}</h1>
            <p>{formState === 'success' ? 'Tu workspace está listo' : 'Empieza tu trial de 14 días hoy'}</p>
          </div>

          {formState === 'success' ? (
            <div className="lp-success-state">
              <div className="lp-success-ring"><CheckCircle2 size={28} /></div>
              <h3>¡Bienvenido, {name.split(' ')[0]}!</h3>
              <p>Tu cuenta fue creada. Redirigiendo al dashboard…</p>
              <div className="lp-progress-bar"><div className="lp-progress-bar__fill" /></div>
            </div>
          ) : (
            <>
              {formState === 'error' && serverErr && (
                <div className="lp-error-banner" style={{ marginBottom: '1rem' }}>
                  <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{serverErr}</span>
                  <button type="button" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 2, display: 'flex' }} onClick={() => { setFormState('idle'); setServerErr(''); }}>
                    <X size={14} />
                  </button>
                </div>
              )}

              <form className="lp-form" onSubmit={handleSubmit} noValidate>
                <div className="lp-field">
                  <label className="lp-label" htmlFor="rp-name">Nombre completo</label>
                  <div className="lp-input-wrap">
                    <span className="lp-input-icon"><User size={15} /></span>
                    <input id="rp-name" ref={nameRef} type="text" autoComplete="name" placeholder="Ana García" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => setNameTouched(true)} className={`lp-input${nameErr && nameTouched ? ' has-error' : name && !nameErr ? ' has-success' : ''}`} disabled={isLoading} />
                    {name && !nameErr && <span style={{ position: 'absolute', right: '0.875rem', color: '#4ade80', display: 'flex' }}><Check size={14} /></span>}
                  </div>
                  {nameErr && nameTouched && <span className="lp-field-error"><AlertCircle size={12} />{nameErr}</span>}
                </div>

                <div className="lp-field">
                  <label className="lp-label" htmlFor="rp-email">Correo electrónico</label>
                  <div className="lp-input-wrap">
                    <span className="lp-input-icon"><Mail size={15} /></span>
                    <input id="rp-email" type="email" autoComplete="email" placeholder="ana@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setEmailTouched(true)} className={`lp-input${emailErr && emailTouched ? ' has-error' : email && !emailErr ? ' has-success' : ''}`} disabled={isLoading} />
                    {email && !emailErr && <span style={{ position: 'absolute', right: '0.875rem', color: '#4ade80', display: 'flex' }}><Check size={14} /></span>}
                  </div>
                  {emailErr && emailTouched && <span className="lp-field-error"><AlertCircle size={12} />{emailErr}</span>}
                </div>

                <div className="lp-field">
                  <label className="lp-label" htmlFor="rp-phone">Teléfono</label>
                  <div className={`rp-phone-wrap${phoneErr && phoneTouched ? ' has-error' : ''}`} ref={countryRef}>
                    <button type="button" className="rp-country-btn" onClick={() => setCountryOpen((o) => !o)} disabled={isLoading} aria-label="Seleccionar país">
                      <span className="rp-country-flag">{country.flag}</span>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem' }}>{country.code}</span>
                      <ChevronDown size={12} style={{ transition: 'transform 0.2s', transform: countryOpen ? 'rotate(180deg)' : 'none', color: '#64748B' }} />

                      {countryOpen && (
                        <div className="rp-country-dropdown">
                          {COUNTRIES.map((c) => (
                            <div
                              key={`${c.name}-${c.code}`}
                              className={`rp-country-option${c.name === country.name ? ' is-selected' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCountry(c);
                                setCountryOpen(false);
                              }}
                            >
                              <span style={{ fontSize: '1rem' }}>{c.flag}</span>
                              <span>{c.name}</span>
                              <span className="rp-country-option__code">{c.code}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </button>

                    <input id="rp-phone" type="tel" autoComplete="tel" placeholder="55 1234 5678" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ''))} onBlur={() => setPhoneTouched(true)} className="rp-phone-input" disabled={isLoading} />
                  </div>
                  {phoneErr && phoneTouched && <span className="lp-field-error"><AlertCircle size={12} />{phoneErr}</span>}
                </div>

                <div className="lp-field">
                  <label className="lp-label" htmlFor="rp-password">Contraseña</label>
                  <div className="lp-input-wrap">
                    <span className="lp-input-icon"><Lock size={15} /></span>
                    <input id="rp-password" type={showPw ? 'text' : 'password'} autoComplete="new-password" placeholder="Mínimo 8 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} onBlur={() => setPasswordTouched(true)} className={`lp-input${passwordErr && passwordTouched ? ' has-error' : ''}`} style={{ paddingRight: '2.75rem' }} disabled={isLoading} />
                    <button type="button" className="lp-pw-toggle" onClick={() => setShowPw((v) => !v)} tabIndex={-1} aria-label={showPw ? 'Ocultar' : 'Mostrar'}>
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {passwordErr && passwordTouched && <span className="lp-field-error"><AlertCircle size={12} />{passwordErr}</span>}

                  {password && (
                    <div className="rp-strength">
                      <div className="rp-strength__bars">
                        {[1, 2, 3, 4].map((i) => <div key={i} className={`rp-strength__bar${strength.score >= i ? ` active-${strength.score}` : ''}`} />)}
                      </div>
                      <span className={`rp-strength__label ${strength.cls}`}>{strength.label}</span>
                    </div>
                  )}

                  {password && (
                    <div className="rp-pw-rules">
                      {pwRules.map((r) => (
                        <span key={r.label} className={`rp-pw-rule${r.met ? ' met' : ''}`}><Check size={10} strokeWidth={3} />{r.label}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="lp-field">
                  <label className="lp-label">Plan de inicio</label>
                  <div className="rp-plan-row">
                    {(plans.length ? plans : [{ id: 'fallback-free', name: 'Free', code: 'free', includedLimits: { rendersMonth: 100 } }]).map((p) => {
                      const renders = Number(p?.includedLimits?.rendersMonth || 0);
                      const rendersLabel = renders >= 1000000 ? `${Math.round(renders / 1000000)}M/mes` : renders >= 1000 ? `${Math.round(renders / 1000)}k/mes` : `${renders}/mes`;
                      const monthlyPrice = Number(p?.monthlyPrice || 0);
                      const priceLabel = monthlyPrice > 0 ? `$${monthlyPrice.toFixed(2)} MXN` : 'Gratis';
                      const isStarter = String(p?.code || '').toLowerCase() === 'starter';
                      return (
                        <label key={p.id} className={`rp-plan-pill${plan === p.id ? ' selected' : ''}`} onClick={() => setPlan(p.id)}>
                          {isStarter && <span className="rp-plan-pill__badge">MÁS POPULAR</span>}
                          <span className="rp-plan-pill__check"><Check size={10} strokeWidth={3} /></span>
                          <span className="rp-plan-pill__name">{p.name}</span>
                          <span className="rp-plan-pill__renders">{rendersLabel} · {priceLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="rp-terms-wrap">
                    <input type="checkbox" checked={terms} onChange={(e) => { setTerms(e.target.checked); if (e.target.checked) setTermsErr(''); }} />
                    <span className="rp-terms-box"><span className="rp-terms-check"><Check size={11} strokeWidth={3} /></span></span>
                    <span className="rp-terms-text">
                      Acepto los <Link to="/terminos-de-servicio" onClick={(e) => e.stopPropagation()}>Términos de servicio</Link> y la <Link to="/politica-de-privacidad" onClick={(e) => e.stopPropagation()}>Política de privacidad</Link> de ReportEngine.
                    </span>
                  </label>
                  {termsErr && <span className="lp-field-error" style={{ marginTop: 6 }}><AlertCircle size={12} />{termsErr}</span>}
                </div>

                <button type="submit" className="lp-submit" disabled={isLoading}>
                  {isLoading ? <><span className="lp-spinner" />{selectedPlanRequiresPayment ? 'Creando cuenta y preparando pago…' : 'Creando cuenta…'}</> : <>Crear cuenta gratis <ArrowRight size={15} /></>}
                </button>

                <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 6px rgba(74,222,128,0.7)' }} />
                  14 días gratis · Cancela cuando quieras · Sin cobros automáticos
                </div>
              </form>

              <p className="lp-signup-prompt">¿Ya tienes cuenta? <Link to="/login" style={{ color: '#818cf8', fontWeight: 500, textDecoration: 'none' }}>Iniciar sesión</Link></p>
            </>
          )}

          <div className="lp-footer-links">
            <Link to="/politica-de-privacidad">Privacidad</Link>
            <span>·</span>
            <Link to="/terminos-de-servicio">Términos</Link>
            <span>·</span>
            <Link to="/estado-del-sistema">Estado</Link>
            <span>·</span>
            <Link to="/documentacion">Help Center</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
